import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';

import { baseAPI } from '~shared/api/base-api';

export {
  getPreviewLineScheduledSuffix,
  isDeferredPreviewLineItem,
} from '~entities/enrollment/lib/preview-line-labels';

export type PreviewEnrollmentEntityType = 'class' | 'event';
export type EnrollmentSource = 'studio_portal' | 'parent_portal';
export type EnrollmentMode = 'manual' | 'bulk' | 'cart' | 'direct' | 'waitlist';

export interface EnrollmentStudentItem {
  studentId: string;
  paymentMethodId?: string;
  /** ENR-3647: optional FamilyContact override for Subscription.parentId */
  payingParentId?: string;
}

/** Per-entity fields — not duplicated on the root payload. */
export interface EnrollmentRequestItem {
  entityId: string;
  entityType: PreviewEnrollmentEntityType;
  students: EnrollmentStudentItem[];
  sessionId?: string;
  collectUpfront?: boolean;
  allowCapacityOverride?: boolean;
  skipPaymentMethod?: boolean;
  skipWallet?: boolean;
  paymentMethodId?: string;
  customCreditAmount?: number;
}

export interface PreviewEnrollmentParams {
  studioId: string;
  requests: EnrollmentRequestItem[];
  source?: EnrollmentSource;
  mode?: EnrollmentMode;
  paymentProvider?: 'stripe' | 'square';
  discountCategory?: string | null;
  discountType?: string;
  discountAmount?: number;
  discountPercentage?: number;
  applyDiscountTo?: string;
}

export interface PreviewChargeLineItem {
  code: string;
  label: string;
  amount: number;
  studentId?: string;
  deferred?: boolean;
  excludedFromAmountDue?: boolean;
}

export interface PreviewChargeBreakdown {
  studentId: string;
  studentName: string;
  currency: string;
  lineItems: PreviewChargeLineItem[];
  tuitionSubtotal: number;
  feesSubtotal: number;
  discountTotal: number;
  walletCreditApplied: number;
  amountDue: number;
  transactionFeeAmount: number;
  amountDueWithTransactionFee: number;
}

export interface PreviewEnrollmentResponse {
  status: number;
  message: string;
  outcome?: 'success' | 'partial' | 'failed';
  existingEnrollmentsList?: string[];
  validation?: unknown;
  charges?: PreviewChargeBreakdown[];
  amountDueTotal?: number;
}

/** Preview/start no-op: already enrolled and nothing new to charge. */
export const isAlreadyEnrolledNoop = (data: {
  existingEnrollmentsList?: string[];
  charges?: unknown[];
  outcome?: string;
}) =>
  !!data.existingEnrollmentsList?.length &&
  !data.charges?.length &&
  data.outcome !== 'partial';

export const previewEnrollment = async (params: PreviewEnrollmentParams) => {
  const response = await baseAPI.post<PreviewEnrollmentResponse>(
    '/v2/enrollments/preview',
    {
      studioId: params.studioId,
      requests: params.requests,
      source: params.source,
      mode: params.mode,
      paymentProvider: params.paymentProvider,
      discountCategory: params.discountCategory ?? undefined,
      discountType: params.discountType,
      discountAmount: params.discountAmount,
      discountPercentage: params.discountPercentage,
      applyDiscountTo: params.applyDiscountTo,
    },
  );
  return response.data;
};

export const usePreviewEnrollment = () => {
  return useMutation({
    mutationFn: previewEnrollment,
    onError: (error: unknown) => {
      const message =
        axios.isAxiosError(error) &&
        typeof error.response?.data === 'object' &&
        error.response.data &&
        'message' in error.response.data &&
        typeof (error.response.data as { message: unknown }).message ===
          'string'
          ? (error.response.data as { message: string }).message
          : 'Failed to preview enrollment charges.';
      toast.error(message);
    },
  });
};
